import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";
import AuthLangSwitch from "../components/AuthLangSwitch";

/**
 * Feb 2026 · Public Gast-Registrierung (5 Sep 2026 · User-Directive).
 *
 * Zwei getrennte, standardmaessig NICHT vorangekreuzte Checkboxen:
 *   1. Pflicht: AGB + Datenschutz
 *   2. Optional: Werbe-Einwilligung (Double-Opt-In per Bestaetigungsmail)
 * Ohne AGB-Haken bleibt der Submit-Button diabled; die Werbe-Checkbox
 * ist rein informativ und wird niemals zur Bedingung fuer die
 * Registrierung.
 */
const AGB_TEXT_DE =
  "Ich akzeptiere die AGB und die Datenschutzerklärung von Weissblom.";
const AGB_TEXT_EN =
  "I accept Weissblom's Terms & Conditions and Privacy Policy.";
const MKT_TEXT_DE =
  "Ich möchte gelegentlich E-Mails mit Angeboten und Neuigkeiten von Weissblom erhalten. (Widerruf jederzeit möglich per Link in jeder E-Mail.)";
const MKT_TEXT_EN =
  "I would like to receive occasional email updates and offers from Weissblom. (Unsubscribe at any time via the link in each email.)";

export default function Signup() {
  const { lang, setLang } = useI18n();
  const nav = useNavigate();
  const de = lang === "de";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agb, setAgb] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    // Feb 2026 · Directive (5 Sep 2026): Signup-Fehlermeldungen strikt
    // DE/EN — daher KEINE `required`-HTML5-Validierung mehr (die zeigt
    // Browser-native Meldungen in OS-Locale). Wir validieren hier
    // explizit und toasten in der aktiven Sprache.
    if (trimmedName.length < 2) {
      toast.error(de ? "Bitte geben Sie Ihren Namen ein." : "Please enter your name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error(de ? "Bitte eine gültige E-Mail-Adresse eingeben." : "Please enter a valid email address.");
      return;
    }
    if (!agb) {
      toast.error(de ? "Bitte AGB und Datenschutz akzeptieren." : "Please accept the Terms & Conditions.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/signup", {
        name: trimmedName,
        email: trimmedEmail.toLowerCase(),
        agb_accepted: true,
        marketing_opt_in: marketing,
        agb_text_shown: de ? AGB_TEXT_DE : AGB_TEXT_EN,
        marketing_text_shown: marketing ? (de ? MKT_TEXT_DE : MKT_TEXT_EN) : null,
      });
      toast.success(
        de
          ? "Wir haben Ihnen einen Zugangscode per E-Mail geschickt."
          : "We have sent your access code by email."
      );
      nav("/login", {
        state: { pendingEmail: trimmedEmail.toLowerCase() },
      });
    } catch (err) {
      const detail = formatErr(err.response?.data?.detail);
      const fallback = de
        ? "Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut."
        : "Sign-up failed. Please try again.";
      toast.error(detail || err.message || fallback);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="signup-page">
      <div
        className="hidden md:flex flex-col justify-between p-12 lg:p-16 text-ivory relative"
        style={{ background: BRAND_COLORS.navy }}
      >
        <div className="flex items-center gap-3" data-testid="signup-brand">
          <img src="/weissblom-logo.webp" alt="Weissblom" className="h-16 w-auto" style={{ objectFit: "contain" }} />
        </div>

        <div className="max-w-md">
          <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] text-ivory mb-6">
            {de ? (
              <>Kostenlos<br />starten.</>
            ) : (
              <>Start<br />for free.</>
            )}
          </h1>
          <p className="text-ivory/80 text-[15px] leading-relaxed">
            {de
              ? "Als Gast können Sie Ihr Profil pflegen, Berichte lesen und mit unserem Team schreiben. Buchungen, Sonderwünsche und Angebote sind Mitgliedern vorbehalten."
              : "As a guest you get profile setup, reading access and team chat. Bookings, special requests and quotes are reserved for members."}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="label-caps text-ivory/50">Private Property Concierge</div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-ivory/60">
            <button data-testid="lang-de" onClick={() => setLang("de")} className={de ? "text-gold" : ""}>DE</button>
            <span className="mx-2 opacity-40">/</span>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={!de ? "text-gold" : ""}>EN</button>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-8 md:p-16" style={{ background: BRAND_COLORS.ivory }}>
        <AuthLangSwitch className="absolute top-6 right-6 md:top-8 md:right-10 z-10" />
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-3 mb-8 p-4" style={{ background: BRAND_COLORS.navy }}>
            <img src="/weissblom-logo.webp" alt="Weissblom" className="h-10 w-auto" style={{ objectFit: "contain" }} />
          </div>

          <div className="mb-8">
            <div className="label-caps mb-3">{de ? "Neu bei Weissblom" : "New to Weissblom"}</div>
            <h2 className="font-display text-4xl md:text-5xl text-navy mb-3">
              {de ? "Gast-Konto anlegen" : "Create guest account"}
            </h2>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed">
              {de
                ? "Kein Passwort nötig — Sie melden sich mit einem 8-stelligen Code aus der E-Mail an."
                : "No password required — sign in with the 8-digit code you'll receive by email."}
            </p>
          </div>

          <form onSubmit={submit} noValidate className="space-y-5" data-testid="signup-form">
            <div>
              <label className="label-caps block mb-2">{de ? "Name" : "Name"}</label>
              <input
                data-testid="signup-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={de ? "Vor- und Nachname" : "First and last name"}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark placeholder:text-navy/30"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">{de ? "E-Mail" : "Email"}</label>
              <input
                data-testid="signup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={de ? "name@beispiel.de" : "name@example.com"}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark placeholder:text-navy/30"
              />
            </div>

            <label className="flex items-start gap-3 pt-2 cursor-pointer" htmlFor="signup-agb-checkbox">
              <input
                id="signup-agb-checkbox"
                data-testid="signup-agb-checkbox"
                type="checkbox"
                checked={agb}
                onChange={(e) => setAgb(e.target.checked)}
                className="mt-1 w-4 h-4 accent-navy shrink-0"
              />
              <span className="text-[13px] text-navy-dark/85 leading-relaxed">
                {de ? AGB_TEXT_DE : AGB_TEXT_EN}{" "}
                <a href="/impressum" target="_blank" rel="noopener noreferrer" className="underline hover:text-gold" data-testid="signup-impressum-link">
                  {de ? "Impressum" : "Imprint"}
                </a>
                {" · "}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-gold" data-testid="signup-datenschutz-link">
                  {de ? "Datenschutz" : "Privacy"}
                </a>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer" htmlFor="signup-marketing-checkbox">
              <input
                id="signup-marketing-checkbox"
                data-testid="signup-marketing-checkbox"
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1 w-4 h-4 accent-navy shrink-0"
              />
              <span className="text-[13px] text-navy-dark/70 leading-relaxed">
                {de ? MKT_TEXT_DE : MKT_TEXT_EN}
              </span>
            </label>

            <button
              data-testid="signup-submit-btn"
              type="submit"
              disabled={loading || !agb}
              className="w-full bg-navy text-ivory py-3.5 text-[13px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {loading ? (de ? "Wird gesendet…" : "Sending…") : de ? "Kostenlos registrieren" : "Sign up free"}
            </button>

            <p className="text-center text-[12px] text-navy-dark/60 pt-2">
              {de ? "Bereits registriert?" : "Already have an account?"}{" "}
              <Link to="/login" data-testid="signup-to-login-link" className="text-navy underline hover:text-gold">
                {de ? "Anmelden" : "Sign in"}
              </Link>
            </p>

            <div className="pt-4 border-t border-navy/10 flex gap-4 flex-wrap">
              <a href="/impressum" data-testid="signup-footer-impressum" className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
                {de ? "Impressum" : "Imprint"}
              </a>
              <a href="/datenschutz" data-testid="signup-footer-datenschutz" className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
                {de ? "Datenschutz" : "Privacy"}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
